<script setup lang="ts">
import { useMarketFilters, type FiltersData, type MarketFilterItem, type SortOption } from './useMarketFilters'

const props = defineProps<{
  languagesList?: MarketFilterItem[]
  coursesList?: MarketFilterItem[]
  productTypesList?: MarketFilterItem[]
  holidaysList?: MarketFilterItem[]
}>()

const emit = defineEmits<{
  (event: 'filters-changed', data: FiltersData): void
}>()

const {
  selectedLanguage,
  searchText,
  isAuthored,
  isOwned,
  isAvailable,
  selectedCourses,
  selectedTypes,
  selectedHolidays,
  priceFrom,
  priceTo,
  currentSort,
  sorts,
  reset
} = useMarketFilters(props, emit)

function changeSort(sort: SortOption) {
  if (currentSort.value === sort.asc) {
    currentSort.value = sort.desc
    return
  }

  if (currentSort.value === sort.desc) {
    currentSort.value = sort.asc
    return
  }

  currentSort.value = sort.desc
}
</script>

<template>
  <div class="market-filters-desktop">
    <div class="d-flex align-center">
      <p class="text-body-bold mr-2">Язык</p>

      <v-button
        v-for="lang in languagesList"
        :key="lang.value"
        class="ml-1"
        :mode="selectedLanguage === lang.value ? 'small flat primary' : 'small text primary'"
        @click="
          () => {
            selectedLanguage = String(lang.value)
          }
        "
      >
        {{ lang.text }}
      </v-button>
    </div>

    <div class="market-filters-desktop__card mt-3">
      <v-search-panel v-model="searchText" placeholder="Поиск по названию товара или ФИО автора"></v-search-panel>

      <div class="d-flex mt-3">
        <v-checkbox v-model="isAuthored">Мои товары</v-checkbox>
        <v-checkbox v-model="isOwned" class="ml-2">Куплено</v-checkbox>
        <v-checkbox v-model="isAvailable" class="ml-2">Доступные</v-checkbox>

        <div class="flex-spacer" />

        <v-link mode="primary" class="text-body-bold" @click="reset"> Сбросить фильтры </v-link>
      </div>

      <div class="d-flex mt-3">
        <v-field>
          <template #label><p class="text-body-bold">Фильтр</p></template>

          <div class="d-flex">
            <v-select
              v-model="selectedCourses"
              placeholder="Курс"
              :items="coursesList"
              multiselect
              :label="selectedCourses.length ? `Курс (${selectedCourses.length})` : null"
            ></v-select>
            <v-select
              v-model="selectedTypes"
              class="ml-2"
              placeholder="Тип товара"
              :items="productTypesList"
              multiselect
              :label="selectedTypes.length ? `Тип товара (${selectedTypes.length})` : null"
            ></v-select>
            <v-select
              v-model="selectedHolidays"
              class="ml-2"
              placeholder="Праздник"
              :items="holidaysList"
              multiselect
              :label="selectedHolidays.length ? `Праздник (${selectedHolidays.length})` : null"
            ></v-select>
          </div>
        </v-field>

        <v-field class="ml-4">
          <template #label><p class="text-body-bold">Стоимость</p></template>

          <div class="d-flex">
            <v-input v-model="priceFrom" placeholder="100">
              <template #prepend>от&nbsp;</template>
            </v-input>

            <v-input v-model="priceTo" class="ml-2" placeholder="100 000">
              <template #prepend>до&nbsp;</template>
            </v-input>
          </div>
        </v-field>
      </div>
    </div>

    <div class="d-flex align-center mt-3">
      <span>Сортировать</span>
      <v-link
        v-for="(sort, index) in sorts"
        :key="index"
        :attr="sort.text"
        class="ml-2"
        :mode="currentSort === sort.asc || currentSort === sort.desc ? 'default' : 'primary'"
        :class="{ _active: currentSort === sort.asc || currentSort === sort.desc }"
        @click="changeSort(sort)"
      >
        {{ sort.text }}
        <v-icon v-if="currentSort === sort.asc" key="asc" class="ml-1"> mdi-sort-reverse-variant </v-icon>
        <v-icon v-else-if="currentSort === sort.desc" key="desc" class="ml-1"> mdi-sort-variant </v-icon>
      </v-link>
    </div>
  </div>
</template>

<style lang="scss">
.market-filters-desktop {
  &__card {
    background: $lightenGray;
    border-radius: 16px;
    padding: 32px;

    display: flex;
    flex-direction: column;
  }
}
</style>
